import React, { useState, useRef, useEffect } from "react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Mic, MicOff, Loader2 } from "lucide-react";

/**
 * VoiceInput - A reusable voice-to-text component
 * Supports multiple languages via browser's Web Speech API
 * 
 * @param {function} onTranscript - Callback when speech is recognized
 * @param {function} onListeningChange - Callback when listening state changes
 * @param {string} className - Additional CSS classes
 * @param {boolean} autoSend - If true, calls onTranscript immediately when speech ends
 * @param {string} size - Button size: "sm", "default", "lg"
 */
export default function VoiceInput({ 
  onTranscript, 
  onListeningChange,
  className = "",
  autoSend = false,
  size = "default",
  disabled = false
}) {
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState("");
  const recognitionRef = useRef(null);
  const isSupported = useRef(false);

  useEffect(() => {
    // Check for Web Speech API support
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      isSupported.current = true;
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      
      // Use browser's detected language or default to user's locale
      const userLang = navigator.language || 'en-US';
      recognitionRef.current.lang = userLang;

      recognitionRef.current.onresult = (event) => {
        let finalTranscript = "";
        let interim = "";
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interim += transcript;
          }
        }
        
        setInterimTranscript(interim);
        
        if (finalTranscript) {
          setIsProcessing(true);
          if (onTranscript) {
            onTranscript(finalTranscript.trim(), autoSend);
          }
          setIsProcessing(false);
          setInterimTranscript("");
        }
      };

      recognitionRef.current.onerror = (event) => {
        console.log("Speech recognition error:", event.error);
        if (event.error === 'not-allowed') {
          toast.error("Microphone access denied. Please enable it in your browser settings.");
        } else if (event.error === 'no-speech') {
          toast.info("No speech detected. Please try again.");
        } else if (event.error !== 'aborted') {
          toast.error(`Voice input error: ${event.error}`);
        }
        setIsListening(false);
        setInterimTranscript("");
        if (onListeningChange) onListeningChange(false);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
        setInterimTranscript("");
        if (onListeningChange) onListeningChange(false);
      };
    }

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // Ignore errors on cleanup
        }
      }
    };
  }, [onTranscript, onListeningChange, autoSend]);

  const toggleListening = () => {
    if (!isSupported.current) {
      toast.error("Voice input is not supported in this browser. Please use Chrome, Edge, or Safari.");
      return;
    }

    if (disabled) return;

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
      if (onListeningChange) onListeningChange(false);
    } else {
      try {
        // Update language to user's current browser language
        const userLang = navigator.language || 'en-US';
        recognitionRef.current.lang = userLang;
        
        recognitionRef.current.start();
        setIsListening(true);
        if (onListeningChange) onListeningChange(true);
        toast.info("🎤 Listening... Speak now", { duration: 2000 });
      } catch (e) {
        if (e.message.includes('already started')) {
          // Already listening, stop and restart
          recognitionRef.current.stop();
          setTimeout(() => {
            recognitionRef.current.start();
            setIsListening(true);
            if (onListeningChange) onListeningChange(true);
          }, 100);
        } else {
          toast.error("Could not start voice input. Please try again.");
          console.error("Voice input error:", e);
        }
      }
    }
  };

  const buttonSizes = {
    sm: "h-8 w-8",
    default: "h-10 w-10",
    lg: "h-12 w-12"
  };

  const iconSizes = {
    sm: "w-4 h-4",
    default: "w-5 h-5",
    lg: "w-6 h-6"
  };

  if (!isSupported.current && typeof window !== 'undefined') {
    // Don't render if not supported
    return null;
  }

  return (
    <div className={`relative inline-flex items-center ${className}`}>
      <Button
        type="button"
        variant={isListening ? "destructive" : "outline"}
        size="icon"
        onClick={toggleListening}
        disabled={disabled || isProcessing}
        className={`${buttonSizes[size]} rounded-full transition-all ${
          isListening 
            ? "bg-red-500 hover:bg-red-600 animate-pulse border-red-500" 
            : "hover:bg-slate-100 border-slate-300"
        }`}
        title={isListening ? "Stop listening" : "Start voice input"}
        data-testid="voice-input-btn"
      >
        {isProcessing ? (
          <Loader2 className={`${iconSizes[size]} animate-spin`} />
        ) : isListening ? (
          <MicOff className={iconSizes[size]} />
        ) : (
          <Mic className={iconSizes[size]} />
        )}
      </Button>
      
      {/* Interim transcript indicator */}
      {interimTranscript && (
        <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-black/75 text-white text-xs px-2 py-1 rounded whitespace-nowrap max-w-48 truncate">
          {interimTranscript}
        </div>
      )}
      
      {/* Listening indicator ring */}
      {isListening && (
        <span className="absolute inset-0 rounded-full animate-ping bg-red-400 opacity-30" />
      )}
    </div>
  );
}
